import { useEffect, useState } from "react";
import { X, ShieldCheck, ShieldX, ShieldAlert, Check } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Opened by scanning a coupon QR (nicabros.com/?verify=CODE). Public read-only status;
// admins additionally get a one-tap "mark redeemed".
export default function VerifyCouponModal({ code, onClose }) {
  const { lang } = useLang();
  const { isAdmin } = useAuth();
  const es = lang === "es";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.verifyCoupon(code).then((r) => setData(r.data)).catch(() => setData({ found: false, status: "invalid", code }));
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const redeem = async () => {
    setBusy(true);
    try { await api.adminRedeemCouponByCode(code); toast.success(es ? "Marcado como canjeado" : "Marked redeemed"); await load(); }
    catch { toast.error(es ? "No se pudo marcar" : "Could not mark"); }
    finally { setBusy(false); }
  };

  const st = data?.status;
  const color = st === "valid" ? "#2e7d32" : st === "redeemed" ? "#0067C6" : "#c62828";
  const Icon = st === "valid" ? ShieldCheck : st === "redeemed" ? ShieldAlert : ShieldX;
  const label = st === "valid" ? (es ? "VÁLIDO" : "VALID") : st === "redeemed" ? (es ? "YA CANJEADO" : "ALREADY REDEEMED") : st === "expired" ? (es ? "VENCIDO" : "EXPIRED") : (es ? "CÓDIGO INVÁLIDO" : "INVALID CODE");
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(es ? "es-NI" : "en-US", { day: "numeric", month: "short", year: "numeric" }) : "");

  return (
    <div data-testid="verify-coupon-modal" className="fixed inset-0 z-[10100] flex items-center justify-center bg-[#0a1c38] p-4" onClick={onClose}>
      <div className="relative w-full max-w-sm pixel-card bg-white text-[#0a1c38] p-5 text-center" onClick={(e) => e.stopPropagation()}>
        <button data-testid="verify-close-btn" onClick={onClose} className="absolute top-2 right-2 text-[#0a1c38]/60"><X size={20} strokeWidth={2.5} /></button>
        <p className="font-pixel text-[9px] text-[#0a1c38]/60">{es ? "VERIFICACIÓN DE CUPÓN · NICABROS" : "COUPON VERIFICATION · NICABROS"}</p>
        {!data ? <p className="font-vt text-lg mt-4">…</p> : (
          <>
            <div className="mx-auto mt-3 w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: color }}>
              <Icon size={34} strokeWidth={2.5} className="text-white" />
            </div>
            <p className="font-pixel text-sm mt-3" style={{ color }} data-testid="verify-status">{label}</p>
            <p className="font-pixel text-[10px] mt-2 break-all" data-testid="verify-code">{data.code}</p>
            {data.found && (
              <div className="font-vt text-lg mt-2 leading-tight" data-testid="verify-details">
                <p>{data.kind === "giftcard" ? `🎁 Gift card $${data.amount} · ${data.brand}` : `🎟️ ${data.percent}% OFF · ${data.brand}`}</p>
                {data.username && <p className="text-[#0a1c38]/70">{es ? "Ganador" : "Winner"}: {data.username}</p>}
                <p className="text-[#0a1c38]/70">{es ? "Vence" : "Expires"}: {fmt(data.expires_at)}{data.redeemed_at ? ` · ${es ? "Canjeado" : "Redeemed"}: ${fmt(data.redeemed_at)}` : ""}</p>
              </div>
            )}
            {isAdmin && data.found && st === "valid" && (
              <button data-testid="verify-redeem-btn" onClick={redeem} disabled={busy} className="mt-4 w-full pixel-btn bg-[#4CAF50] text-white px-4 py-3 text-[10px] flex items-center justify-center gap-1.5 disabled:opacity-60">
                <Check size={14} strokeWidth={3} /> {es ? "Marcar como canjeado" : "Mark as redeemed"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
